
<div class="space-y-6 max-w-4xl mx-auto">
  <!-- Header -->
  <div>
    <h1 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
      <i class="fa-solid fa-user-circle text-emerald-600"></i> ข้อมูลส่วนตัว (My Profile)
    </h1>
    <p class="text-gray-600 mt-1">จัดการข้อมูลบัญชีผู้ใช้งานของคุณ</p>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
    <!-- Left Column: User Info (Always Visible) -->
    <!-- If canSign is false, this will take up a logical space, effectively centering the focus -->
    <div [class]="canSign() ? 'md:col-span-1' : 'md:col-span-3 max-w-lg mx-auto w-full'">
      <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div class="bg-slate-50 p-6 flex flex-col items-center border-b border-slate-200">
          <div class="w-24 h-24 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-4xl font-bold mb-4 shadow-inner">
            {{ user()?.name?.charAt(0) }}
          </div>
          <h2 class="text-xl font-bold text-gray-800">{{ user()?.name }}</h2>
          <span class="inline-block bg-slate-200 text-slate-600 text-xs px-3 py-1 rounded-full mt-2 font-bold uppercase tracking-wider">
            {{ user()?.role }}
          </span>
        </div>
        <div class="p-6 space-y-4">
          <div>
            <label class="text-xs font-bold text-slate-400 uppercase">อีเมล (Email)</label>
            <p class="text-sm font-medium text-gray-700 break-all">{{ user()?.email }}</p>
          </div>
          <div>
            <label class="text-xs font-bold text-slate-400 uppercase">รหัสพนักงาน (UID)</label>
            <p class="text-xs font-mono text-gray-500 bg-slate-50 p-2 rounded border border-slate-100 truncate">{{ user()?.uid }}</p>
          </div>
          <div>
             <label class="text-xs font-bold text-slate-400 uppercase">PIN Access</label>
             <p class="text-sm font-medium text-gray-700">
                @if(user()?.pin) {
                    <span class="text-emerald-600"><i class="fa-solid fa-check-circle"></i> ตั้งค่าแล้ว</span>
                } @else {
                    <span class="text-red-500"><i class="fa-solid fa-times-circle"></i> ยังไม่ได้ตั้งค่า</span>
                }
             </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Digital Signature (Restricted Access) -->
    @if(canSign()) {
        <div class="md:col-span-2 space-y-6 animate-fade-in-fast">
           <!-- Signature Card -->
           <div class="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div class="flex justify-between items-start mb-4">
                  <div>
                      <h3 class="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <i class="fa-solid fa-file-signature text-blue-500"></i> ลายเซ็นดิจิทัล (Digital Signature)
                      </h3>
                      <p class="text-sm text-slate-500 mt-1">ใช้สำหรับลงนามในเอกสารใบรับรองแพทย์และรายงาน ภท.</p>
                  </div>
                  @if(isSaving()) {
                      <span class="text-sm text-blue-600 font-bold animate-pulse">
                          <i class="fa-solid fa-spinner fa-spin mr-1"></i> กำลังบันทึก...
                      </span>
                  }
              </div>

              <!-- Current Signature Preview -->
              <div class="mb-6">
                  <p class="text-xs font-bold text-slate-400 uppercase mb-2">ลายเซ็นปัจจุบัน (Current)</p>
                  <div class="h-32 w-full bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-center overflow-hidden relative">
                      @if(signaturePreview()) {
                          <img [src]="signaturePreview()" class="max-h-full max-w-full object-contain mix-blend-multiply">
                          <div class="absolute top-2 right-2 bg-green-100 text-green-700 text-[10px] px-2 py-0.5 rounded font-bold border border-green-200">Active</div>
                      } @else {
                          <div class="text-slate-400 text-sm flex flex-col items-center">
                              <i class="fa-solid fa-signature text-2xl mb-1 opacity-30"></i>
                              <span>ยังไม่มีลายเซ็น</span>
                          </div>
                      }
                  </div>
              </div>

              <!-- Drawing Pad -->
              <div>
                  <div class="flex justify-between items-end mb-2">
                      <p class="text-xs font-bold text-slate-400 uppercase">สร้างลายเซ็นใหม่ (New Signature)</p>
                      <button (click)="clearSignature()" class="text-xs text-red-500 hover:text-red-700 underline">ล้าง (Clear)</button>
                  </div>
                  
                  <div class="border-2 border-dashed border-slate-300 rounded-xl bg-white relative touch-none shadow-inner" style="height: 200px;">
                      <canvas #signaturePad class="w-full h-full cursor-crosshair rounded-xl"></canvas>
                      <div class="absolute bottom-2 right-3 text-[10px] text-slate-300 pointer-events-none select-none">
                          Sign here using mouse or finger
                      </div>
                  </div>

                  <div class="flex flex-col sm:flex-row gap-3 mt-4">
                      <button (click)="saveSignature()" [disabled]="isSaving()" class="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 rounded-lg shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                          บันทึก (Save Signature)
                      </button>
                      
                      <label [class]="'flex-1 border border-slate-300 text-slate-600 hover:bg-slate-50 font-bold py-2.5 rounded-lg text-center cursor-pointer transition-colors ' + (isSaving() ? 'opacity-50 cursor-not-allowed' : '')">
                          <input type="file" (change)="onFileSelected($event)" accept="image/*" class="hidden" [disabled]="isSaving()">
                          <i class="fa-solid fa-upload mr-2"></i> อัปโหลดรูปภาพ
                      </label>
                  </div>
                  <p class="text-xs text-slate-400 mt-2 text-center">* แนะนำให้ใช้รูปภาพพื้นหลังโปร่งใส (Transparent PNG) หรือเซ็นสดบนหน้าจอ</p>
              </div>
           </div>
        </div>
    }
  </div>
</div>
